import { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import ToolPanel from '../components/ToolPanel';
import TipsWindow from '../components/TipsWindow';
import { useTip } from '../hooks/useTips';
import { COLORS, SLIDER_STYLES, TYPOGRAPHY_STYLES, FORM_STYLES } from '../styles/viewerStyles';
import { TRANSFORM_DEFAULT } from '../utils/transformationCodec';

// ─── Field / group config ─────────────────────────────────────────────────────

const FIELDS = [
  { key: 'tx', min: -200, max: 200, step: 0.05,  decimals: 2 },
  { key: 'ty', min: -200, max: 200, step: 0.05,  decimals: 2 },
  { key: 'tz', min: -200, max: 200, step: 0.05,  decimals: 2 },
  { key: 'rx', min: -180, max: 180, step: 0.1,  decimals: 1 },
  { key: 'ry', min: -180, max: 180, step: 0.1,  decimals: 1 },
  { key: 'rz', min: -180, max: 180, step: 0.1,  decimals: 1 },
  { key: 'sx', min: 0.01, max: 5,   step: 0.01, decimals: 2 },
  { key: 'sy', min: 0.01, max: 5,   step: 0.01, decimals: 2 },
  { key: 'sz', min: 0.01, max: 5,   step: 0.01, decimals: 2 },
];

const FIELD_MAP = Object.fromEntries(FIELDS.map(f => [f.key, f]));

const GROUPS = [
  { i18nKey: 'Translation', keys: ['tx', 'ty', 'tz'] },
  { i18nKey: 'Rotation',    keys: ['rx', 'ry', 'rz'] },
  { i18nKey: 'Scale',       keys: ['sx', 'sy', 'sz'] },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(value, decimals) {
  return Number(value).toFixed(decimals);
}

function buildTexts(vals) {
  const texts = {};
  FIELDS.forEach(f => { texts[f.key] = fmt(vals[f.key] ?? TRANSFORM_DEFAULT[f.key], f.decimals); });
  return texts;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Props supplied by BaseCesiumViewer via useTransformationTool:
 *   pointValues / gsValues      — current committed transform values (persisted across panel open/close)
 *   pointValuesRef / gsValuesRef — refs to latest values (for stale-closure-safe reads)
 *   applyPoint(field, value)    — update + apply to point cloud tileset
 *   applyGs(field, value)       — update + apply to gaussian tileset
 */
function TransformationTools({
  onClose,
  tileset,
  gaussianTileset,
  pointValues,
  gsValues,
  pointValuesRef,
  gsValuesRef,
  applyPoint,
  applyGs,
  onSave,
}) {
  const { t } = useTranslation();

  const hasPoints = Boolean(tileset);
  const hasGs     = Boolean(gaussianTileset);
  const hasBoth   = hasPoints && hasGs;


  const [target, setTarget] = useState(() => hasPoints ? 'points' : 'gs');

  // Text input state — initialized from the currently displayed (persisted) values
  const [inputTexts, setInputTexts] = useState(() =>
    buildTexts(hasPoints ? pointValues : gsValues)
  );

  // Save status: null | 'success' | 'error'
  const [saveStatus, setSaveStatus] = useState(null);
  const saveStatusTimerRef = useRef(null);

  // ── Tips ─────────────────────────────────────────────────────────────────

  const { isOpen: isTipOpen, title: tipTitle, markdownBody: tipBody, close: closeTip } = useTip('transformation', true);

  // ── Target selector ──────────────────────────────────────────────────────

  const handleTargetChange = useCallback((newTarget) => {
    setTarget(newTarget);
    const vals = newTarget === 'gs' ? gsValuesRef.current : pointValuesRef.current;
    setInputTexts(buildTexts(vals));
  }, [gsValuesRef, pointValuesRef]);

  // ── Core apply (routes to the right tileset(s)) ──────────────────────────

  const applyField = useCallback((field, value) => {
    if (target === 'points') applyPoint(field, value);
    else                     applyGs(field, value);
  }, [target, applyPoint, applyGs]);

  // ── Slider onChange (fires on every drag step) ───────────────────────────

  const handleSliderChange = useCallback((field, value) => {
    applyField(field, value);
    setInputTexts(prev => ({ ...prev, [field]: fmt(value, FIELD_MAP[field].decimals) }));
  }, [applyField]);

  // ── Text field handlers ──────────────────────────────────────────────────

  const handleTextChange = useCallback((field, text) => {
    setInputTexts(prev => ({ ...prev, [field]: text }));
  }, []);

  const handleTextCommit = useCallback((field) => {
    const fieldDef = FIELD_MAP[field];
    const parsed = parseFloat(inputTexts[field]);
    if (isNaN(parsed)) {
      const cur = target === 'gs' ? gsValuesRef.current : pointValuesRef.current;
      setInputTexts(prev => ({ ...prev, [field]: fmt(cur[field], fieldDef.decimals) }));
      return;
    }
    const clamped = Math.max(fieldDef.min, Math.min(fieldDef.max, parsed));
    applyField(field, clamped);
    setInputTexts(prev => ({ ...prev, [field]: fmt(clamped, fieldDef.decimals) }));
  }, [target, inputTexts, applyField, gsValuesRef, pointValuesRef]);


  // ── Step buttons (±1 step) ───────────────────────────────────────────────

  const handleStep = useCallback((field, direction) => {
    const fieldDef = FIELD_MAP[field];
    const cur = target === 'gs' ? gsValuesRef.current : pointValuesRef.current;
    const next = Math.max(
      fieldDef.min,
      Math.min(fieldDef.max, parseFloat((cur[field] + direction * fieldDef.step).toFixed(fieldDef.decimals + 1)))
    );
    applyField(field, next);
    setInputTexts(prev => ({ ...prev, [field]: fmt(next, fieldDef.decimals) }));
  }, [target, applyField, gsValuesRef, pointValuesRef]);

  // ── Save & restore ───────────────────────────────────────────────────────

  const handleSave = useCallback(async () => {
    if (!onSave) return;
    try {
      await onSave(target);
      setSaveStatus('success');
    } catch {
      setSaveStatus('error');
    }
    clearTimeout(saveStatusTimerRef.current);
    saveStatusTimerRef.current = setTimeout(() => setSaveStatus(null), 3000);
  }, [onSave, target]);

  const handleRestoreDefault = useCallback(() => {
    FIELDS.forEach(f => applyField(f.key, TRANSFORM_DEFAULT[f.key]));
    setInputTexts(buildTexts(TRANSFORM_DEFAULT));
    setSaveStatus(null);
  }, [applyField]);

  // ── Displayed slider values ──────────────────────────────────────────────

  const displayedValues = target === 'gs' ? gsValues : pointValues;

  // ── Row renderer ─────────────────────────────────────────────────────────

  const renderRow = (fieldDef) => {
    const { key, min, max, step } = fieldDef;
    const value = displayedValues[key] ?? 0;

    return (
      <Box key={key} sx={{ display: 'flex', alignItems: 'center', mb: 0.5, mr: 0.5 }}>
        <Typography
          sx={{
            ...TYPOGRAPHY_STYLES.captionSmall,
            minWidth: 20,
            fontFamily: 'monospace',
            textAlign: 'right',
            mr: 1,
            opacity: 0.7,
          }}
        >
          {key}
        </Typography>

        <Slider
          value={typeof value === 'number' ? value : 0}
          min={min}
          max={max}
          step={step}
          onChange={(_, v) => handleSliderChange(key, v)}
          sx={{ ...SLIDER_STYLES.small, flex: 1, mx: 1 }}
        />

        <IconButton
          size="small"
          onClick={() => handleStep(key, -1)}
          sx={{ color: COLORS.white, p: '2px', opacity: 0.7, '&:hover': { opacity: 1 } }}
        >
          <RemoveIcon sx={{ fontSize: 14 }} />
        </IconButton>

        <InputBase
          value={inputTexts[key] ?? ''}
          onChange={e => handleTextChange(key, e.target.value)}
          onBlur={() => handleTextCommit(key)}
          onKeyDown={e => { if (e.key === 'Enter') handleTextCommit(key); }}
          inputProps={{
            style: {
              textAlign: 'right',
              fontFamily: 'monospace',
              fontSize: '0.68rem',
              padding: '2px 4px',
              color: COLORS.white,
            },
          }}
          sx={{
            width: 62,
            bgcolor: 'rgba(255,255,255,0.07)',
            borderRadius: '4px',
            border: '1px solid rgba(255,255,255,0.12)',
            '&:focus-within': { borderColor: 'rgba(255,255,255,0.35)' },
          }}
        />

        <IconButton
          size="small"
          onClick={() => handleStep(key, +1)}
          sx={{ color: COLORS.white, p: '2px', opacity: 0.7, '&:hover': { opacity: 1 } }}
        >
          <AddIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
    );
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <>
    <TipsWindow isOpen={isTipOpen} title={tipTitle} markdownBody={tipBody} onClose={closeTip} />
    <ToolPanel title={t('TransformationTool')} onClose={onClose}>

      {/* Dataset selector row — shown when both types present */}
      {hasBoth && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, mr: 1.5 }}>
          <Typography sx={{ ...TYPOGRAPHY_STYLES.captionSmall, opacity: 0.7, whiteSpace: 'nowrap' }}>
            {t('Dataset')}
          </Typography>
          <FormControl sx={{ flex: 1 }}>
            <Select
              value={target}
              onChange={e => handleTargetChange(e.target.value)}
              size="small"
              sx={FORM_STYLES.select}
              MenuProps={FORM_STYLES.selectMenuProps}
            >
              <MenuItem value="points">
                <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>{t('PointCloud')}</Typography>
              </MenuItem>
              <MenuItem value="gs">
                <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>{t('GaussianSplatting')}</Typography>
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Translation / Rotation / Scale groups */}
      {GROUPS.map((group, gi) => (
        <Box key={group.i18nKey}>
          {gi > 0 && (
            <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.08)' }} />
          )}
          <Typography
            sx={{
              ...TYPOGRAPHY_STYLES.captionSmall,
              mb: 0.75,
              opacity: 0.55,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            {t(group.i18nKey)}
          </Typography>
          {FIELDS.filter(f => group.keys.includes(f.key)).map(renderRow)}
        </Box>
      ))}

      {/* Bottom action bar */}
      {onSave && (
        <>
          <Divider sx={{ mt: 1.5, mb: 1, borderColor: 'rgba(255,255,255,0.08)' }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton
              onClick={handleSave}
              title={t('SaveTransformation')}
              sx={{ color: '#ffffff', p: 0.5, '&:hover': { bgcolor: 'rgba(182,182,182,0.2)' } }}
            >
              <SaveIcon sx={{ fontSize: '16px' }} />
            </IconButton>
            <IconButton
              onClick={handleRestoreDefault}
              title={t('RestoreDefault')}
              sx={{ color: '#ffffff', p: 0.5, '&:hover': { bgcolor: 'rgba(182,182,182,0.2)' } }}
            >
              <RestoreIcon sx={{ fontSize: '16px' }} />
            </IconButton>
          </Box>
          {saveStatus && (
            <Box sx={{ mt: 1, p: 0.75, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 1, mr: 1 }}>
              <Typography variant="caption" sx={{ color: '#fff' }}>
                {saveStatus === 'success' ? t('TransformationSaved') : t('TransformationSaveFailed')}
              </Typography>
            </Box>
          )}
        </>
      )}
    </ToolPanel>
    </>
  );
}

export default TransformationTools;
